// Performance Detection Utility
// Detects device capabilities and assigns performance tiers

export const getDeviceTier = () => {
  // Get device memory (if available)
  const deviceMemory = navigator.deviceMemory || 4; // Default to 4GB if not available
  
  // Get hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 4; // Default to 4 cores
  
  // Get screen size and pixel ratio
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const pixelRatio = window.devicePixelRatio || 1;
  
  // Calculate total pixels
  const totalPixels = screenWidth * screenHeight * pixelRatio;
  
  // Performance tier calculation (reduced motion only affects animation speed, not star count)
  let tier;
  if (deviceMemory >= 8 && cores >= 8 && totalPixels >= 2073600) {
    tier = 'high'; // High-end: 8GB+ RAM, 8+ cores, 1080p+
  } else if (deviceMemory >= 4 && cores >= 4 && totalPixels >= 921600) {
    tier = 'medium'; // Medium: 4GB+ RAM, 4+ cores, 720p+
  } else if (deviceMemory >= 2 && cores >= 2 && totalPixels >= 480000) {
    tier = 'mobile'; // Mobile: 2GB+ RAM, 2+ cores, 480p+
  } else {
    tier = 'low'; // Low-end: Less than mobile specs
  }
  
  return tier;
};

export const getStarCount = (tier, screenArea = null) => {
  // Improved star counts by device tier (slightly higher for better balance)
  const baseStarCounts = {
    high: 1200,
    medium: 900,
    mobile: 600,
    low: 300
  };
  
  const baseCount = baseStarCounts[tier] || 300;
  
  // If screen area is provided, calculate screen-based stars
  if (screenArea) {
    const screenStars = Math.round(screenArea / 1800); // Slightly more stars per screen area
    // Use the smaller of device limit or screen calculation
    return Math.min(baseCount, screenStars);
  }
  
  return baseCount;
};

export const shouldRenderStars = (tier) => {
  // Always render stars - just with different counts based on device tier
  return true;
};

export const getPerformanceSettings = (tier) => {
  const settings = {
    high: {
      pixelRatio: [1, 2],
      antialias: true,
      shadows: true,
      dpr: [1, 2]
    },
    medium: {
      pixelRatio: [1, 1.5],
      antialias: true,
      shadows: false,
      dpr: [1, 1.5]
    },
    mobile: {
      pixelRatio: [1, 1],
      antialias: false,
      shadows: false,
      dpr: [1, 1]
    },
    low: {
      pixelRatio: [1, 1],
      antialias: false,
      shadows: false,
      dpr: [1, 1]
    }
  };
  
  return settings[tier] || settings.low;
};

export const getAnimationSpeed = () => {
  // Check for reduced motion preference
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reducedMotion ? 0.3 : 1; // Slower animation if reduced motion is preferred
};
